/**
 * Geometry Extraction Utilities
 *
 * Extracts 3D geometry data from parsed IDF documents for rendering.
 */

import type { IDFDocument, IDFObject } from '../parser/types';
import type {
  BuildingGeometry,
  BoundaryCondition,
  FenestrationGeometry,
  FenestrationType,
  ShadingGeometry,
  SurfaceGeometry,
  SurfaceType,
  Vector3,
  ZoneGeometry,
} from './types';

/**
 * Parse surface type from EnergyPlus string.
 */
function parseSurfaceType(typeStr: string): SurfaceType {
  const normalized = typeStr.toLowerCase().trim();
  switch (normalized) {
    case 'wall':
      return 'wall';
    case 'floor':
      return 'floor';
    case 'roof':
      return 'roof';
    case 'ceiling':
      return 'ceiling';
    default:
      return 'wall';
  }
}

/**
 * Parse fenestration type from EnergyPlus string.
 */
function parseFenestrationType(typeStr: string): FenestrationType {
  const normalized = typeStr.toLowerCase().trim();
  switch (normalized) {
    case 'window':
      return 'window';
    case 'door':
      return 'door';
    case 'glassdoor':
    case 'glass door':
      return 'glassdoor';
    default:
      return 'window';
  }
}

/**
 * Parse boundary condition from EnergyPlus string.
 */
function parseBoundaryCondition(conditionStr: string): BoundaryCondition {
  const normalized = conditionStr.toLowerCase().trim();
  switch (normalized) {
    case 'outdoors':
      return 'outdoors';
    case 'ground':
      return 'ground';
    case 'adiabatic':
      return 'adiabatic';
    case 'surface':
      return 'surface';
    case 'zone':
      return 'zone';
    case 'othersidecoefficients':
      return 'othersidecoefficients';
    case 'othersideconditionsmodel':
      return 'othersideconditionsmodel';
    case 'groundfcfactormethod':
      return 'groundfcfactormethod';
    case 'groundslabpreprocessoraverage':
      return 'groundslabpreprocessoraverage';
    case 'groundslabpreprocessorcore':
      return 'groundslabpreprocessorcore';
    case 'groundslabpreprocessorperimeter':
      return 'groundslabpreprocessorperimeter';
    case 'groundbasementpreprocessoraveragewall':
      return 'groundbasementpreprocessoraveragewall';
    case 'groundbasementpreprocessoraveragefloor':
      return 'groundbasementpreprocessoraveragefloor';
    case 'groundbasementpreprocessorupperwall':
      return 'groundbasementpreprocessorupperwall';
    case 'groundbasementpreprocessorlowerwall':
      return 'groundbasementpreprocessorlowerwall';
    case 'foundation':
      return 'foundation';
    default:
      return 'unknown';
  }
}

/**
 * Parse vertices from IDF object fields.
 * Vertices start at a specific index and are in groups of 3 (x, y, z).
 */
function parseVertices(obj: IDFObject, startIndex: number): Vector3[] {
  const vertices: Vector3[] = [];

  for (let i = startIndex; i < obj.fields.length; i += 3) {
    const xField = obj.fields[i];
    const yField = obj.fields[i + 1];
    const zField = obj.fields[i + 2];

    if (xField && yField && zField) {
      const x = parseFloat(xField.value);
      const y = parseFloat(yField.value);
      const z = parseFloat(zField.value);

      if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
        vertices.push({ x, y, z });
      }
    }
  }

  return vertices;
}

/**
 * Calculate the normal vector for a surface defined by vertices.
 * Uses the first three vertices to compute a normal using the cross product.
 */
export function calculateNormal(vertices: Vector3[]): Vector3 {
  if (vertices.length < 3) {
    return { x: 0, y: 0, z: 1 };
  }

  // We've already checked vertices.length >= 3, so these are guaranteed to exist
  const [v0, v1, v2] = vertices as [Vector3, Vector3, Vector3];

  // Edge vectors
  const e1 = { x: v1.x - v0.x, y: v1.y - v0.y, z: v1.z - v0.z };
  const e2 = { x: v2.x - v0.x, y: v2.y - v0.y, z: v2.z - v0.z };

  // Cross product
  const normal = {
    x: e1.y * e2.z - e1.z * e2.y,
    y: e1.z * e2.x - e1.x * e2.z,
    z: e1.x * e2.y - e1.y * e2.x,
  };

  // Normalize
  const length = Math.sqrt(normal.x * normal.x + normal.y * normal.y + normal.z * normal.z);
  if (length > 0) {
    normal.x /= length;
    normal.y /= length;
    normal.z /= length;
  }

  return normal;
}

/**
 * Calculate the centroid of a set of vertices.
 */
export function calculateCentroid(vertices: Vector3[]): Vector3 {
  if (vertices.length === 0) {
    return { x: 0, y: 0, z: 0 };
  }

  const sum = vertices.reduce(
    (acc, v) => ({ x: acc.x + v.x, y: acc.y + v.y, z: acc.z + v.z }),
    { x: 0, y: 0, z: 0 }
  );

  return {
    x: sum.x / vertices.length,
    y: sum.y / vertices.length,
    z: sum.z / vertices.length,
  };
}

/**
 * Calculate the centroid of a zone based on all its surface vertices.
 */
function calculateZoneCentroid(surfaces: SurfaceGeometry[]): Vector3 {
  const allVertices = surfaces.flatMap((s) => s.vertices);
  return calculateCentroid(allVertices);
}

/**
 * Parse a Zone object from IDF.
 */
function parseZone(obj: IDFObject): Omit<ZoneGeometry, 'surfaces' | 'centroid'> {
  const fields = obj.fields;
  const startLine = obj.sourceLocation?.startLine;

  return {
    id: obj.id,
    name: fields[0]?.value ?? '',
    directionOfRelativeNorth: parseFloat(fields[1]?.value ?? '0') || 0,
    origin: {
      x: parseFloat(fields[2]?.value ?? '0') || 0,
      y: parseFloat(fields[3]?.value ?? '0') || 0,
      z: parseFloat(fields[4]?.value ?? '0') || 0,
    },
    type: parseInt(fields[5]?.value ?? '1') || 1,
    multiplier: parseInt(fields[6]?.value ?? '1') || 1,
    ...(startLine !== undefined && { sourceLine: startLine }),
  };
}

/**
 * Parse a BuildingSurface:Detailed object from IDF.
 * Field indices:
 * 0: Name
 * 1: Surface Type
 * 2: Construction Name
 * 3: Zone Name
 * 4: Space Name (optional, can be empty)
 * 5: Outside Boundary Condition
 * 6: Outside Boundary Condition Object
 * 7: Sun Exposure
 * 8: Wind Exposure
 * 9: View Factor to Ground
 * 10: Number of Vertices
 * 11+: Vertices (X, Y, Z triplets)
 */
function parseBuildingSurface(obj: IDFObject): Omit<SurfaceGeometry, 'fenestrations'> {
  const fields = obj.fields;
  const startLine = obj.sourceLocation?.startLine;

  const vertices = parseVertices(obj, 11);
  const normal = calculateNormal(vertices);

  const outsideBoundaryObject = fields[6]?.value || undefined;

  return {
    id: obj.id,
    name: fields[0]?.value ?? '',
    type: parseSurfaceType(fields[1]?.value ?? ''),
    construction: fields[2]?.value ?? '',
    zoneName: fields[3]?.value ?? '',
    boundaryCondition: parseBoundaryCondition(fields[5]?.value ?? ''),
    ...(outsideBoundaryObject && { outsideBoundaryObject }),
    sunExposure: (fields[7]?.value ?? '').toLowerCase().includes('sun'),
    windExposure: (fields[8]?.value ?? '').toLowerCase().includes('wind'),
    vertices,
    normal,
    ...(startLine !== undefined && { sourceLine: startLine }),
  };
}

/**
 * Parse a FenestrationSurface:Detailed object from IDF.
 * Field indices:
 * 0: Name
 * 1: Surface Type
 * 2: Construction Name
 * 3: Building Surface Name
 * 4: Outside Boundary Condition Object
 * 5: View Factor to Ground
 * 6: Frame and Divider Name
 * 7: Multiplier
 * 8: Number of Vertices
 * 9+: Vertices (X, Y, Z triplets)
 */
function parseFenestration(obj: IDFObject): FenestrationGeometry {
  const fields = obj.fields;
  const startLine = obj.sourceLocation?.startLine;

  const vertices = parseVertices(obj, 9);
  const normal = calculateNormal(vertices);

  return {
    id: obj.id,
    name: fields[0]?.value ?? '',
    type: parseFenestrationType(fields[1]?.value ?? ''),
    construction: fields[2]?.value ?? '',
    parentSurface: fields[3]?.value ?? '',
    vertices,
    normal,
    ...(startLine !== undefined && { sourceLine: startLine }),
  };
}

/**
 * Parse a shading surface object from IDF.
 */
function parseShadingSurface(
  obj: IDFObject,
  shadingType: 'site' | 'building' | 'zone'
): ShadingGeometry {
  const fields = obj.fields;
  const startLine = obj.sourceLocation?.startLine;

  // Different shading types have different field structures
  let name: string;
  let transmittanceSchedule: string | undefined;
  let zoneName: string | undefined;
  let vertexStartIndex: number;

  if (shadingType === 'site') {
    // Shading:Site:Detailed
    // 0: Name, 1: Transmittance Schedule, 2: Number of Vertices, 3+: Vertices
    name = fields[0]?.value ?? '';
    transmittanceSchedule = fields[1]?.value || undefined;
    vertexStartIndex = 3;
  } else if (shadingType === 'building') {
    // Shading:Building:Detailed
    // 0: Name, 1: Transmittance Schedule, 2: Number of Vertices, 3+: Vertices
    name = fields[0]?.value ?? '';
    transmittanceSchedule = fields[1]?.value || undefined;
    vertexStartIndex = 3;
  } else {
    // Shading:Zone:Detailed
    // 0: Name, 1: Base Surface Name, 2: Transmittance Schedule, 3: Number of Vertices, 4+: Vertices
    name = fields[0]?.value ?? '';
    zoneName = fields[1]?.value || undefined;
    transmittanceSchedule = fields[2]?.value || undefined;
    vertexStartIndex = 4;
  }

  const vertices = parseVertices(obj, vertexStartIndex);
  const normal = calculateNormal(vertices);

  return {
    id: obj.id,
    name,
    shadingType,
    ...(zoneName && { zoneName }),
    ...(transmittanceSchedule && { transmittanceSchedule }),
    vertices,
    normal,
    ...(startLine !== undefined && { sourceLine: startLine }),
  };
}

/**
 * Calculate the bounding box of the entire geometry.
 */
function calculateBoundingBox(zones: ZoneGeometry[], shadingSurfaces: ShadingGeometry[]): {
  min: Vector3;
  max: Vector3;
} {
  const allVertices: Vector3[] = [
    ...zones.flatMap((z) => z.surfaces.flatMap((s) => s.vertices)),
    ...shadingSurfaces.flatMap((s) => s.vertices),
  ];

  if (allVertices.length === 0) {
    return {
      min: { x: 0, y: 0, z: 0 },
      max: { x: 0, y: 0, z: 0 },
    };
  }

  const min = {
    x: Math.min(...allVertices.map((v) => v.x)),
    y: Math.min(...allVertices.map((v) => v.y)),
    z: Math.min(...allVertices.map((v) => v.z)),
  };

  const max = {
    x: Math.max(...allVertices.map((v) => v.x)),
    y: Math.max(...allVertices.map((v) => v.y)),
    z: Math.max(...allVertices.map((v) => v.z)),
  };

  return { min, max };
}

/**
 * Extract geometry from an IDF document.
 */
export function extractGeometry(document: IDFDocument): BuildingGeometry {
  const zonesMap = new Map<string, ZoneGeometry>();
  const surfacesMap = new Map<string, SurfaceGeometry>();
  const shadingSurfaces: ShadingGeometry[] = [];

  // First pass: collect zones
  for (const obj of document.objects) {
    if (obj.className.toLowerCase() === 'zone') {
      const zone = parseZone(obj);
      zonesMap.set(zone.name.toLowerCase(), {
        ...zone,
        surfaces: [],
        centroid: { x: 0, y: 0, z: 0 },
      });
    }
  }

  // Second pass: collect building surfaces
  for (const obj of document.objects) {
    if (obj.className.toLowerCase() === 'buildingsurface:detailed') {
      const surface = parseBuildingSurface(obj);
      const fullSurface: SurfaceGeometry = { ...surface, fenestrations: [] };

      surfacesMap.set(surface.name.toLowerCase(), fullSurface);

      // Add surface to its zone
      const zone = zonesMap.get(surface.zoneName.toLowerCase());
      if (zone) {
        zone.surfaces.push(fullSurface);
      }
    }
  }

  // Third pass: collect fenestrations and attach to parent surfaces
  for (const obj of document.objects) {
    if (obj.className.toLowerCase() === 'fenestrationsurface:detailed') {
      const fenestration = parseFenestration(obj);

      // Find parent surface and add fenestration
      const parentSurface = surfacesMap.get(fenestration.parentSurface.toLowerCase());
      if (parentSurface) {
        parentSurface.fenestrations.push(fenestration);
      }
    }
  }

  // Fourth pass: collect shading surfaces
  for (const obj of document.objects) {
    const className = obj.className.toLowerCase();
    if (className === 'shading:site:detailed') {
      shadingSurfaces.push(parseShadingSurface(obj, 'site'));
    } else if (className === 'shading:building:detailed') {
      shadingSurfaces.push(parseShadingSurface(obj, 'building'));
    } else if (className === 'shading:zone:detailed') {
      shadingSurfaces.push(parseShadingSurface(obj, 'zone'));
    }
  }

  // Calculate zone centroids
  const zones = Array.from(zonesMap.values());
  for (const zone of zones) {
    zone.centroid = calculateZoneCentroid(zone.surfaces);
  }

  // Calculate bounding box
  const boundingBox = calculateBoundingBox(zones, shadingSurfaces);

  // Calculate center
  const center = {
    x: (boundingBox.min.x + boundingBox.max.x) / 2,
    y: (boundingBox.min.y + boundingBox.max.y) / 2,
    z: (boundingBox.min.z + boundingBox.max.z) / 2,
  };

  return {
    zones,
    shadingSurfaces,
    boundingBox,
    center,
  };
}

/**
 * Get a flat list of all surfaces from the geometry.
 */
export function getAllSurfaces(geometry: BuildingGeometry): SurfaceGeometry[] {
  return geometry.zones.flatMap((z) => z.surfaces);
}

/**
 * Get a flat list of all fenestrations from the geometry.
 */
export function getAllFenestrations(geometry: BuildingGeometry): FenestrationGeometry[] {
  return geometry.zones.flatMap((z) => z.surfaces.flatMap((s) => s.fenestrations));
}

/**
 * Find a surface by ID.
 */
export function findSurfaceById(
  geometry: BuildingGeometry,
  id: string
): SurfaceGeometry | undefined {
  for (const zone of geometry.zones) {
    for (const surface of zone.surfaces) {
      if (surface.id === id) {
        return surface;
      }
    }
  }
  return undefined;
}

/**
 * Find a fenestration by ID.
 */
export function findFenestrationById(
  geometry: BuildingGeometry,
  id: string
): FenestrationGeometry | undefined {
  for (const zone of geometry.zones) {
    for (const surface of zone.surfaces) {
      for (const fenestration of surface.fenestrations) {
        if (fenestration.id === id) {
          return fenestration;
        }
      }
    }
  }
  return undefined;
}

/**
 * Find a zone by ID.
 */
export function findZoneById(geometry: BuildingGeometry, id: string): ZoneGeometry | undefined {
  return geometry.zones.find((z) => z.id === id);
}
